import { useTranslation } from 'next-i18next';
import upperFirst from 'lodash/upperFirst';
import React from 'react';
import StoreFront from '../../common/Icons/StoreFront';
import Smoking from '../../common/Icons/Smoking';
import Parking from '../../common/Icons/Parking';
import Wifi from '../../common/Icons/Wifi';
import Door from '../../common/Icons/Door';
import Bathtub from '../../common/Icons/Bathtub';
import Electric from '../../common/Icons/Electric';
import Restaurant from '../../common/Icons/Restaurant';
import Onsen from '../../common/Icons/Onsen';
import type { Hotel } from '../../../core/isValidHotel';
import testIds from './testIds';
import tailwindConfig from '../../../../tailwind.config';
import Television from '../../common/Icons/Television';
import RoomWear from '../../common/Icons/RoomWear';
import Service from '../../common/Icons/Service';
import ShuttleBus from '../../common/Icons/ShuttleBus';
import Laundry from '../../common/Icons/Laundry';
import IceMachine from '../../common/Icons/IceMachine';
import Disabled from '../../common/Icons/Disabled';
import { FACILITY_DESCRIPTION } from '../../../core/constants';
import SkipTranslationText from '../../common/SkipTranslationText';
import SkipTranslationWrapper from '../../common/SkipTranslationWrapper';

type ListItemProps = {
  title: string;
  description: string | number | string[] | React.ReactElement;
  testId: string;
  icon?: React.ReactNode;
};

const ListItem = ({ title, description, testId, icon }: ListItemProps) => {
  let items: string[] = [];
  if (Array.isArray(description)) {
    items = description.reduce((accumulator: string[], current: string) => {
      if (current.includes('/')) {
        return [...accumulator, ...current.split('/')];
      }
      return [...accumulator, current];
    }, []);
  } else if (typeof description === 'string' && description.includes('/')) {
    items = description.split('/');
  }

  return (
    <>
      {/* Mobile */}
      <div data-testid={`${testId}-mobile`} className="mb-6 flex flex-col gap-2 sm:hidden">
        <div className="flex items-center gap-4">
          {icon}
          <p data-testid={testIds.listitem_title} className="text-regular font-bold">
            {title}
          </p>
        </div>
        <div
          className="ml-10 whitespace-pre-wrap text-small text-gray-600"
          data-testid={testIds.listitem_description}
        >
          {items?.length > 0
            ? items?.map((item, index) => (
                <>
                  <p
                    key={`${title}_${item}`}
                    className="m-0 inline"
                  >{`${typeof item === 'string' ? upperFirst(item.trim()) : item}`}</p>
                  <SkipTranslationWrapper tag="span">
                    {index < items.length - 1 ? ' / ' : ''}
                  </SkipTranslationWrapper>
                </>
              ))
            : description}
        </div>
      </div>

      {/* Desktop */}
      <div
        data-testid={`${testId}-desktop`}
        className="mb-6 hidden break-inside-avoid flex-col gap-2 sm:flex"
      >
        <div className="flex items-center gap-4">
          {icon}
          <p data-testid={testIds.listitem_title} className="text-regular font-bold">
            {title}
          </p>
        </div>
        <div className="ml-10 flex flex-col " data-testid={testIds.listitem_description}>
          {items?.length > 0 ? (
            items?.map((item) => (
              <p
                key={`${title}_${item}`}
                className="whitespace-pre-wrap text-small text-gray-600"
              >{`${typeof item === 'string' ? upperFirst(item.trim()) : item}`}</p>
            ))
          ) : (
            <p className="whitespace-pre-wrap text-small text-gray-600">{description}</p>
          )}
        </div>
      </div>
    </>
  );
};

type ListProps = {
  title: string;
  children: Array<string | boolean | JSX.Element>;
};

const List = ({ title, children }: ListProps) => {
  return (
    <div className="flex flex-col border-gray-200 bg-white pt-4 sm:flex-row sm:gap-4 sm:border-t sm:pt-6">
      <div className="sm:w-1/4">
        <p className="mb-6 text-large font-bold sm:font-normal">{title}</p>
      </div>
      <div className="sm:flex sm:flex-1">
        <div
          className="column-1 w-full sm:columns-3"
          style={{ columnFill: 'balance', columnGap: '24px' }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

type Props = {
  hotel?: Hotel;
};

const getBathroom = (hotel: Hotel) => {
  const bathroom: string[] = [];
  if (hotel?.roomAmenities?.toothbrush?.value) {
    bathroom.push(hotel?.roomAmenities?.toothbrush?.description);
  }
  if (hotel?.roomAmenities?.shampoo?.value) {
    bathroom.push(hotel?.roomAmenities?.shampoo?.description);
  }
  if (hotel.roomAmenities?.towel?.value) {
    bathroom.push(hotel.roomAmenities?.towel?.description);
  }
  if (hotel?.roomAmenities?.hairDryer?.value) {
    bathroom.push(hotel.roomAmenities?.hairDryer?.description);
  }
  if (hotel.roomAmenities?.razor?.value) {
    bathroom.push(hotel.roomAmenities?.razor?.description);
  }
  if (hotel.roomAmenities?.cosmetic?.value) {
    bathroom.push(hotel.roomAmenities?.cosmetic?.description);
  }
  if (hotel.roomAmenities?.showerCap?.value) {
    bathroom.push(hotel.roomAmenities?.showerCap?.description);
  }

  return bathroom;
};

const getRoomWear = (hotel: Hotel) => {
  const roomWear: string[] = [];
  if (hotel.roomAmenities?.slippers?.value) {
    roomWear.push(hotel.roomAmenities?.slippers?.description);
  }
  if (hotel.roomAmenities?.yukata?.value) {
    roomWear.push(hotel.roomAmenities?.yukata?.description);
  }

  return roomWear;
};

const getEntertainment = (hotel: Hotel) => {
  const entertainment: string[] = [];
  if (hotel.roomAmenities?.wifi?.value) {
    entertainment.push(hotel.roomAmenities?.wifi?.description);
  }
  if (hotel.roomAmenities?.tv?.value) {
    entertainment.push(hotel.roomAmenities?.tv?.description);
  }
  if (hotel.roomAmenities?.telephone?.value) {
    entertainment.push(hotel.roomAmenities?.telephone?.description);
  }

  return entertainment;
};

const getElectric = (hotel: Hotel) => {
  const electric: string[] = [];
  if (hotel.roomAmenities?.refrigerator?.value) {
    electric.push(hotel.roomAmenities?.refrigerator?.description);
  }
  if (hotel.roomAmenities?.electricPot?.value) {
    electric.push(hotel.roomAmenities?.electricPot?.description);
  }
  if (hotel.roomAmenities?.moisturizer?.value) {
    electric.push(hotel.roomAmenities?.moisturizer?.description);
  }
  if (hotel.roomAmenities?.iron?.value) {
    electric.push(hotel.roomAmenities?.iron?.description);
  }
  if (hotel.roomAmenities?.safebox?.value) {
    electric.push(hotel.roomAmenities?.safebox?.description);
  }

  return electric;
};

const getInternets = (hotel: Hotel) => {
  const internets: string[] = [];
  if (hotel.internet) {
    internets.push(hotel.internet);
  }
  if (hotel?.facilities?.wifi?.value) {
    internets.push(hotel?.facilities?.wifi?.description);
  }

  return internets;
};

const getMeals = (hotel: Hotel) => {
  const meals: string[] = [];
  if (hotel.meal) {
    meals.push(hotel.meal);
  }
  if (hotel?.facilities?.restaurant?.value) {
    meals.push(hotel?.facilities?.restaurant?.description);
  }

  return meals;
};

const getSmokingPolicies = (hotel: Hotel) => {
  const smokingPolicies: string[] = [];
  if (hotel.smokingDetails) {
    smokingPolicies.push(hotel.smokingDetails);
  }
  if (hotel?.facilities?.noSmoking?.value) {
    smokingPolicies.push(hotel?.facilities?.noSmoking?.description);
  }

  return smokingPolicies;
};

const mapFacilityStatusToLabel = (
  item: { description: string; value: boolean } | undefined,
  label: string
) => {
  if (!item?.value) {
    return null;
  }

  switch (item.description) {
    // available
    case FACILITY_DESCRIPTION.AVAILABLE_JA:
    case FACILITY_DESCRIPTION.AVAILABLE_EN:
      return label;

    // unavailable
    case FACILITY_DESCRIPTION.UNAVAILABLE_JA:
    case FACILITY_DESCRIPTION.UNAVAILABLE_EN:
      return null;

    default:
      return item.description;
  }
};

export const getOnsenDescription = (hotel: Hotel) => {
  const onsenDescriptions: string[] = [];
  if (hotel?.facilities?.publicBath?.value) {
    onsenDescriptions.push(hotel?.facilities?.publicBath?.description);
  }
  if (hotel?.facilities?.spa?.value) {
    onsenDescriptions.push(hotel?.facilities?.spa?.description);
  }
  return onsenDescriptions;
};

const FacilitiesAndRoom = ({ hotel }: Props) => {
  const { t } = useTranslation('hotel');
  const bathroom = hotel ? getBathroom(hotel) : [];
  const entertainment = hotel ? getEntertainment(hotel) : [];
  const electric = hotel ? getElectric(hotel) : [];
  const roomWear = hotel ? getRoomWear(hotel) : [];
  const roomOther =
    (hotel?.roomAmenities?.others ?? []).map((amenity) => amenity.description) || [];
  const facilityOther =
    (hotel?.facilities?.others ?? []).map((facility) => facility.description) || [];

  const onsenDescription = hotel ? getOnsenDescription(hotel) : [];

  const services = [
    mapFacilityStatusToLabel(hotel?.facilities?.frontDesk24, t('facilities_and_room.front_24h')),
    mapFacilityStatusToLabel(
      hotel?.facilities?.automaticFrontDesk,
      t('facilities_and_room.front_automatic')
    ),
  ].filter((x) => x !== null);

  const facilityAmenity = [
    mapFacilityStatusToLabel(hotel?.facilities?.meetingRoom, t('facilities_and_room.meeting_room')),
    mapFacilityStatusToLabel(hotel?.facilities?.iceMachine, t('facilities_and_room.ice_machine')),
  ].filter((x) => x !== null);

  const internets = hotel ? getInternets(hotel) : [];

  const meals = hotel ? getMeals(hotel) : [];

  const smokingPolicies = hotel ? getSmokingPolicies(hotel) : [];

  const parking = () => {
    if (!hotel?.parking) {
      return t('facilities_and_room.none');
    }

    switch (hotel?.parking) {
      // available
      case FACILITY_DESCRIPTION.AVAILABLE_JA:
      case FACILITY_DESCRIPTION.AVAILABLE_EN:
        return t('facilities_and_room.parking');
      case '':
      case undefined:
        return t('facilities_and_room.none');
      default:
        return hotel?.parking;
    }
  };

  const shuttle = () => {
    const pickUpService = hotel?.facilities?.pickUpService;
    if (!pickUpService?.value) {
      return t('facilities_and_room.none');
    }

    switch (pickUpService.description) {
      // unavailable
      case FACILITY_DESCRIPTION.UNAVAILABLE_JA:
      case FACILITY_DESCRIPTION.UNAVAILABLE_EN:
      case undefined:
        return t('facilities_and_room.none');
      default:
        return pickUpService.description;
    }
  };

  const laundry = (() => {
    const laundryService = hotel?.facilities?.laundry;
    if (
      !laundryService?.value ||
      laundryService.description === FACILITY_DESCRIPTION.UNAVAILABLE_JA ||
      laundryService.description === FACILITY_DESCRIPTION.UNAVAILABLE_EN
    ) {
      return null;
    }
    return laundryService.description;
  })();

  const numOfRooms = Number(hotel?.numOfRooms);

  return (
    <>
      <h3 className="app-section-title pb-0 sm:pb-6">{t('facilities_and_room.title')}</h3>
      <div className="flex flex-col gap-4 sm:gap-6">
        <List title={t('facilities_and_room.facility_equipments')}>
          {Number.isFinite(numOfRooms) && numOfRooms > 0 && (
            <ListItem
              testId={testIds.num_of_rooms}
              icon={<Door size={24} strokeColor={tailwindConfig.theme.colors.gray[950]} />}
              title={t('facilities_and_room.number_of_rooms')}
              description={
                <SkipTranslationText
                  i18nKey="common:label.room_count"
                  values={{ count: numOfRooms }}
                />
              }
            />
          )}
          {services.length > 0 && (
            <ListItem
              testId={testIds.services}
              title={t('facilities_and_room.services')}
              description={services}
              icon={<Service size={24} color={tailwindConfig.theme.colors.gray[950]} />}
            />
          )}
          {internets.length > 0 && (
            <ListItem
              testId={testIds.wifi}
              title={t('facilities_and_room.internet')}
              description={internets}
              icon={<Wifi size={24} color={tailwindConfig.theme.colors.gray[950]} />}
            />
          )}
          {meals.length > 0 && (
            <ListItem
              testId={testIds.meal}
              icon={<Restaurant size={24} strokeColor={tailwindConfig.theme.colors.gray[950]} />}
              title={t('facilities_and_room.meal')}
              description={meals}
            />
          )}
          {onsenDescription.length > 0 && (
            <ListItem
              testId={testIds.onsen}
              icon={<Onsen size={24} color={tailwindConfig.theme.colors.gray[950]} />}
              title={t('facilities_and_room.onsen')}
              description={onsenDescription}
            />
          )}
          {smokingPolicies.length > 0 && (
            <ListItem
              testId={testIds.smoking}
              icon={<Smoking size={24} color={tailwindConfig.theme.colors.gray[950]} />}
              title={t('facilities_and_room.smoking')}
              description={smokingPolicies}
            />
          )}
          <ListItem
            testId={testIds.parking}
            icon={<Parking size={24} color={tailwindConfig.theme.colors.gray[950]} />}
            title={t('facilities_and_room.parking')}
            description={parking()}
          />
          <ListItem
            testId={testIds.pick_up_service}
            icon={<ShuttleBus size={24} color={tailwindConfig.theme.colors.gray[950]} />}
            title={t('facilities_and_room.pick_up_service')}
            description={shuttle()}
          />
          {!!laundry && (
            <ListItem
              testId={testIds.laundry}
              icon={<Laundry size={24} color={tailwindConfig.theme.colors.gray[950]} />}
              title={t('facilities_and_room.laundry')}
              description={laundry}
            />
          )}
          {facilityAmenity.length > 0 && (
            <ListItem
              testId={testIds.facilities_amenities}
              icon={<IceMachine size={24} color={tailwindConfig.theme.colors.gray[950]} />}
              title={t('facilities_and_room.facilities_amenities')}
              description={facilityAmenity}
            />
          )}
          {!!hotel?.accessibility && (
            <ListItem
              testId={testIds.accessibility}
              icon={<Disabled size={24} color={tailwindConfig.theme.colors.gray[950]} />}
              title={t('facilities_and_room.accessibility')}
              description={hotel.accessibility}
            />
          )}
          {facilityOther.length > 0 && (
            <ListItem
              testId={testIds.facility_other}
              icon={<StoreFront size={24} color={tailwindConfig.theme.colors.gray[950]} />}
              title={t('facilities_and_room.others')}
              description={facilityOther}
            />
          )}
        </List>

        <div className="border-t border-gray-200 sm:border-none">
          <List title={t('facilities_and_room.room_facilities')}>
            {entertainment.length > 0 && (
              <ListItem
                testId={testIds.entertainment_connectivity}
                icon={<Television size={24} color={tailwindConfig.theme.colors.gray[950]} />}
                title={t('facilities_and_room.entertainment_connectivity')}
                description={entertainment}
              />
            )}
            {electric.length > 0 && (
              <ListItem
                testId={testIds.comfort_security}
                icon={<Electric size={24} color={tailwindConfig.theme.colors.gray[950]} />}
                title={t('facilities_and_room.comfort_security')}
                description={electric}
              />
            )}
            {bathroom.length > 0 && (
              <ListItem
                testId={testIds.bathroom}
                icon={<Bathtub size={24} color={tailwindConfig.theme.colors.gray[950]} />}
                title={t('facilities_and_room.bath_room')}
                description={bathroom}
              />
            )}
            {roomWear.length > 0 && (
              <ListItem
                testId={testIds.room_wear}
                icon={<RoomWear size={24} color={tailwindConfig.theme.colors.gray[950]} />}
                title={t('facilities_and_room.room_wear')}
                description={roomWear}
              />
            )}
            {roomOther.length > 0 && (
              <ListItem
                testId={testIds.room_other}
                icon={<StoreFront size={24} color={tailwindConfig.theme.colors.gray[950]} />}
                title={t('facilities_and_room.others')}
                description={roomOther}
              />
            )}
          </List>
        </div>
      </div>
    </>
  );
};

export default FacilitiesAndRoom;
